class AddToCart extends HTMLElement {
  constructor() {
    super();
    this.isLoading = false;
    this.isSuccess = false;
  }

  connectedCallback() {
    this.setupEventListeners();
  }

  get variantId() {
    return this.getAttribute('variant-id');
  }

  get quantity() {
    return parseInt(this.getAttribute('quantity') || '1');
  }

  get defaultClasses() {
    return this.getAttribute('data-default-classes') || 'btn w-full text-center flex justify-center items-center';
  }

  get loadingClasses() {
    return this.getAttribute('data-loading-classes') || 'btn w-full text-center flex justify-center items-center opacity-75 cursor-not-allowed';
  }

  get successClasses() {
    return this.getAttribute('data-success-classes') || 'btn w-full text-center flex justify-center items-center bg-green-600 hover:bg-green-700 text-white';
  }

  get errorClasses() {
    return this.getAttribute('data-error-classes') || 'btn w-full text-center flex justify-center items-center bg-red-600 hover:bg-red-700 text-white';
  }

  get defaultText() {
    return this.getAttribute('data-default-text') || 'Add to cart';
  }

  get loadingText() {
    return this.getAttribute('data-loading-text') || 'Adding...';
  }

  get successText() {
    return this.getAttribute('data-success-text') || 'Added to cart!';
  }

  get errorText() {
    return this.getAttribute('data-error-text') || 'Try again';
  }

  setupEventListeners() {
    this.addEventListener('click', (e) => {
      e.preventDefault();
      this.handleAddToCart();
    });
  }

  async handleAddToCart() {
    if (this.isLoading || !this.variantId) {
      return;
    }

    this.setState('loading');

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items: [{
            id: this.variantId,
            quantity: this.quantity
          }]
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to add item to cart');
      }

      const result = await response.json();
      
      this.dispatchEvent(new CustomEvent('cart-add-success', {
        detail: { items: result.items, quantity: this.quantity },
        bubbles: true
      }));

      document.querySelector('.cart-item-count').classList.add('animate-ping');

      this.setState('success');
      this.updateCartCount();

      setTimeout(() => {
        this.setState('default');
      }, 3000);

      setTimeout(() => {
        document.querySelector('.cart-item-count').classList.remove('animate-ping');
      }, 600);

    } catch (error) {
      console.error('Error adding to cart:', error);
      
      this.dispatchEvent(new CustomEvent('cart-add-error', {
        detail: { error: error.message },
        bubbles: true
      }));

      this.setState('error');

      setTimeout(() => {
        this.setState('default');
      }, 3000);
    }
  }

  setState(state) {
    const button = this.tagName === 'BUTTON' ? this : this.querySelector('button');
    if (!button) return;

    // Store original text if not already stored
    if (!button.dataset.originalText) {
      button.dataset.originalText = button.textContent.trim();
    }

    // Remove all state classes
    button.className = '';

    switch (state) {
      case 'loading':
        this.isLoading = true;
        button.disabled = true;
        button.className = this.loadingClasses;
        button.innerHTML = `
          <svg class="w-4 h-4 animate-spin mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
          </svg>
          ${this.loadingText}
        `;
        break;

      case 'success':
        this.isLoading = false;
        button.disabled = false;
        button.className = this.successClasses;
        button.innerHTML = `
          <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
          </svg>
          ${this.successText}
        `;
        break;

      case 'error':
        this.isLoading = false;
        button.disabled = false;
        button.className = this.errorClasses;
        button.innerHTML = `
          <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
          ${this.errorText}
        `;
        break;

      case 'default':
      default:
        this.isLoading = false;
        button.disabled = false;
        button.className = this.defaultClasses;
        button.textContent = button.dataset.originalText || this.defaultText;
        break;
    }
  }

  async updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      this.dispatchEvent(new CustomEvent('cart-count-update', {
        detail: { count: cart.item_count, total: cart.total_price },
        bubbles: true
      }));

      const cartCountElements = document.querySelectorAll('[data-cart-count]');
      cartCountElements.forEach(element => {
        element.textContent = cart.item_count;
      });

    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  }
}

customElements.define('add-to-cart', AddToCart);